<script setup lang="ts">
import TasksKanbanFilter from '@/components/tasks-kanban/components/TasksKanbanFilter.vue';
import TaskHeader from '@/pages/tasks-order-pages/components/task-header/task-header.vue';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPagination,
  TirPmScrollbar,
  TirPmTooltipElement,
} from 'tir-components';
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import {
  PageRequestModel,
  PaginationRequestModel,
  PaginationResponseModel,
  ProjectModel,
  UserModel,
} from '@/models';
import { debounce } from 'tir-pm-debounce';
import ProjectTaskLi from '@/pages/tasks-order-pages/components/project-tasks/project-task-li.vue';
import { TaskDrawer, TaskDrawerSettingsModel, TasksKanbanFilterModel } from '@/components';
import { ArrowUturnLeftIcon, MagnifyingGlassIcon } from 'tir-style-system/icons/outline';
import { TaskViewElementEnum } from './enums';
import {
  GetProjectTasksRequestModel,
  OrderTaskModel,
  TaskColumnModel,
  TaskFilterModel,
  TaskSortingModel,
  TaskModel,
} from './models';
import { AdapterService } from './adapter-service';
import UserTasksSkeleton from './components/user-tasks-skeleton/user-tasks-skeleton.vue';

/**Параметры */
const props = defineProps({
  /**ID выбранного юзера */
  userId: { type: Number, required: true },
  /**ID проекта */
  projectId: { type: Number, required: true },
});
/**События */
const emit = defineEmits<{
  /**Событие добавление задачи в очередность выполнения */
  add: [task: OrderTaskModel];
  /**Событие открытия шторки задачи */
  openTaskDrawer: [taskId: number];
  /**Удаление задачи */
  removeTask: [taskId: number];
}>();

/**Роутер */
const router = useRouter();
/**адаптер */
const adapter = new AdapterService();

/**Колонки */
const columns: TaskColumnModel[] = [
  new TaskColumnModel({
    Type: TaskViewElementEnum.Namespace,
    Name: '',
    Width: '1.5rem',
    MinWidth: '1.5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Queue,
    Name: 'Очередь',
    Width: '13.375rem',
    MinWidth: '13.375rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Number,
    Name: '№',
    Width: '5rem',
    MinWidth: '5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Name,
    Name: 'Название',
    Width: '50vw',
    MinWidth: '20rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Status,
    Name: 'Статус',
    Width: '5.1875rem',
    MinWidth: '5.1875rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Priority,
    Name: 'Приоритет',
    Width: '4rem',
    MinWidth: '4rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Difficulty,
    Name: 'Сложность',
    Width: '2rem',
    MinWidth: '2rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.EstimateTime,
    Name: 't (оценка)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.WorkTime,
    Name: 't (затрачено)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
];
/**Дефолтное кол-во элементов на странице */
const DEFAULT_PAGE_STEP = 10;

/**Текущий исполнитель */
const currentPerformer = ref<UserModel>();
/**Список задач */
const tasks = ref<TaskModel[]>([]);
/**Страница */
const pageResponse = ref<PaginationResponseModel<Array<TaskModel>>>();
/**Загрузка при сортировке */
const isLoadingSorting = ref(false);
/**Кол-во элементов на странице */
const pageStep = ref(DEFAULT_PAGE_STEP);
/**Ссылка на компонент шторки задач */
const $taskDrawer = ref<InstanceType<typeof TaskDrawer>>();
/**Информация проекта */
const projectInfo = ref<ProjectModel>();
/**Данные сортировки */
const sortingData = ref(
  new TaskSortingModel({
    OrderById: true,
  }),
);
/**Загрузка */
const isLoading = ref<boolean>();
/**Фильтр */
const filter = ref(new TasksKanbanFilterModel());
/**Настройки видимости */
const viewSettings = ref([
  TaskViewElementEnum.Namespace,
  TaskViewElementEnum.Queue,
  TaskViewElementEnum.Name,
  TaskViewElementEnum.Number,
  TaskViewElementEnum.Status,
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
]);

/**Текущая страница */
const currentPageRequest = computed(() => {
  if (pageResponse.value)
    return new PaginationRequestModel({
      Skip: pageResponse.value.Size * (pageResponse.value.Current - 1),
      Take: pageStep.value,
    });
});
/**Кол-во страниц */
const pagesCount = computed(
  () => pageResponse.value && Math.ceil(pageResponse.value.Count / pageResponse.value.Size),
);

/**Получить информацию о юзере */
const getUserInfo = async () => {
  isLoading.value = true;
  const result = await adapter.getEmployee(props.userId);
  if (result.IsSuccess) {
    currentPerformer.value = result.Value;
  }
  isLoading.value = false;
};
/** Получить список задач */
const getTasks = async (page: PaginationRequestModel, clear?: boolean) => {
  isLoading.value = true;
  if (clear) tasks.value = [];

  const response = await adapter.getTasksByProjectId(
    new GetProjectTasksRequestModel({
      ProjectId: props.projectId,
      Filter: new TaskFilterModel({
        Performers: [props.userId],
        Search: filter.value.Search,
      }),
      Page: page,
      Sort: sortingData.value,
    }),
  );
  if (response.IsSuccess) {
    tasks.value = response?.Items || [];
    pageResponse.value = response;
  } else {
    TirPmNotificationEventBus.emit(
      'add',
      new TirPmNotificationModel({
        title: response.ErrorMessage ?? 'Ошибка получения списка задач',
        type: 'error',
        group: 'general',
        life: 5000,
        closable: true,
      }),
    );
  }
  isLoading.value = false;
  return response.IsSuccess;
};
/**Получить информацию проекта */
const getProjectInfo = async () => {
  const resp = await adapter.getProject(props.projectId);
  if (resp.IsSuccess) {
    projectInfo.value = resp.Value;
  }
};
const comeBack = () => {
  router.back();
};

/**Изменить кол-во элементов на странице */
const changePageStep = async (value: number) => {
  pageStep.value = value;
  await getTasks(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};

/**Обновить текущую страницу */
const changeCurrentPage = async (value: number) => {
  if (!pageResponse.value) return;

  await getTasks(
    new PaginationRequestModel({
      Skip: pageResponse.value.Size * (value - 1),
      Take: pageStep.value,
    }),
  );
};

/**Обновить тип сортировки */
const updateSortData = async (data: TaskSortingModel) => {
  if (!currentPageRequest.value) return;
  isLoadingSorting.value = true;
  sortingData.value = new TaskSortingModel(data);
  await getTasks(currentPageRequest.value);
  isLoadingSorting.value = false;
};

/**Открыть шторку с задачей */
const openTaskDrawer = (taskId: number) => {
  $taskDrawer.value?.open(
    new TaskDrawerSettingsModel({
      Mode: 'edit',
      TaskId: taskId,
    }),
  );
};

/**При поиске */
const onSearch = async () => {
  if (!currentPageRequest.value) return;

  await getTasks(currentPageRequest.value, true);
};

/**При поиске через debounce */
const debounceOnSearch = debounce(onSearch, 100);
const applyFilter = async () => {
  if (!currentPageRequest.value) return;

  await getTasks(currentPageRequest.value, true);
};

onMounted(async () => {
  await getProjectInfo();
  await getUserInfo();
  await getTasks(
    new PaginationRequestModel(
      new PageRequestModel({
        Skip: 0,
        Take: pageStep.value,
      }),
    ),
  );
});
</script>
<template>
  <div class="user-tasks-page__content">
    <div class="user-tasks-page__block">
      <div class="user-tasks-page__data-user">
        <TirPmButton
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Regular"
          @click="comeBack"
        >
          <template #leftSlot>
            <ArrowUturnLeftIcon />
          </template>
        </TirPmButton>
        <TirPmIconView
          :size="24"
          isRounded
          :isBordered="false"
          :colorCode="currentPerformer?.AvatarColor"
          :imageLoadUrl="currentPerformer?.ImageUrl"
        >
          <template #mainSlot>
            {{ currentPerformer?.LastName[0] }}{{ currentPerformer?.FirstName[0] }}
          </template>
        </TirPmIconView>
        <div class="user-tasks-page__data-user-name">
          {{ currentPerformer?.LastName }} {{ currentPerformer?.FirstName }}
          {{ currentPerformer?.MiddleName }}: все задачи по проекту:
        </div>
        <TirPmTooltipElement width="max-content">
          <TirPmIconView
            :size="24"
            isRounded
            :isBordered="false"
            :colorCode="projectInfo?.PreviewColor"
            :imageLoadUrl="projectInfo?.ImageUrl"
          >
            <template #mainSlot>
              {{ projectInfo?.Name[0] }}
            </template>
          </TirPmIconView>
          <template #content>
            {{ projectInfo?.Name }}
          </template>
        </TirPmTooltipElement>
      </div>
      <div class="user-tasks-page__filter-header">
        <TasksKanbanFilter
          ref="$tasksKanbanFilter"
          v-model:view-elements="viewSettings"
          v-model:filter="filter"
          isSortingAndGroupingHidden
          isFilterHidden
          :tasks="tasks"
          :projectId="+projectId"
          @on-search="debounceOnSearch"
          @applyFilter="applyFilter"
        />
      </div>
      <UserTasksSkeleton v-if="isLoading" />
      <div v-if="tasks?.length && !isLoading" class="user-tasks-page__main">
        <TaskHeader
          class="user-tasks-page__columns-header"
          monitoringPage
          :sort-data="sortingData"
          :columns="columns"
          :enable-sorting="true"
          :view-settings="viewSettings"
          :is-loading="isLoadingSorting"
          @update:sort-data="updateSortData"
        />
        <TirPmScrollbar class="user-tasks-page__scrollbar" showOnHover>
          <div class="user-tasks-page__list">
            <ProjectTaskLi
              v-for="task in tasks"
              monitoringPage
              monitoringTicketPage
              :key="task.Id"
              :task="task"
              :columns="columns"
              :view-settings="viewSettings"
              @open-task-drawer="openTaskDrawer($event)"
              @removeTask="changeCurrentPage"
            />
          </div>
        </TirPmScrollbar>
      </div>
      <div v-if="!tasks?.length && !isLoading" class="user-tasks-page__no-find-tasks">
        <TirPmIconView :size="80" :isBordered="false">
          <template #mainSlot>
            <MagnifyingGlassIcon />
          </template>
        </TirPmIconView>
        <div class="user-tasks-page__no-find-tasks--title">Тикеты не найдены</div>
      </div>
      <TirPmPagination
        v-if="pageResponse?.Current && pagesCount"
        class="user-tasks-page__paginator"
        showMoreElements
        :model-value="pageResponse.Current"
        :range-size="3"
        :pages="pagesCount"
        :options="[10, 20, 30, 40, 50, 100]"
        :count-elements="pageStep"
        @update:count-elements="changePageStep"
        @update:model-value="changeCurrentPage"
      />
    </div>
    <TaskDrawer ref="$taskDrawer" :showCloseButton="false" />
  </div>
</template>
<style scoped lang="scss">
.user-tasks-page {
  &__data-user-name {
    color: var(--text-secondary, #686c73);
    font-size: 1.25rem;
    font-weight: 600;
  }
  &__no-find-tasks {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    height: 100%;
  }
  &__no-find-tasks--title {
    font-size: 1.625rem;
    font-weight: 500;
  }
  &__data-user {
    display: flex;
    align-items: center;
    margin-bottom: 0.625rem;
    gap: 0.75rem;
  }
  &__filter-header {
    margin-bottom: 1.5rem;
  }
  &__content {
    padding: 1rem 1.25rem;
  }
  &__block {
    position: relative;
    display: flex;
    flex-direction: column;
    height: calc(100vh - 9.875rem);
    background-color: var(--bg-base, #fff);
    padding: 1.5rem;
    border-radius: 0.5rem;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  &__paginator {
    position: absolute;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 1rem;
  }
}
</style>

<style lang="scss">
.user-tasks-page {
  &__scrollbar {
    max-height: calc(100vh - 26rem);
    .horizontal-line {
      height: 100%;
    }
  }
}
</style>
